import React, { useState, useEffect } from 'react';
import {
  FiUsers, FiShoppingBag, FiPackage, FiHome,
  FiDollarSign, FiTrendingUp, FiCalendar, FiBarChart2,
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import adminService from '../../services/admin.service';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Simple bar chart component (no external dependency)
const BarChart = ({ data, maxBars = 30 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <FiBarChart2 size={32} className="mb-2 d-block mx-auto" />
        <small>No revenue data yet</small>
      </div>
    );
  }

  const displayData = data.slice(-maxBars);
  const maxRevenue = Math.max(...displayData.map((d) => parseFloat(d.revenue) || 0), 1);

  return (
    <div>
      <div className="d-flex align-items-end gap-1" style={{ height: '180px' }}>
        {displayData.map((day, idx) => {
          const revenue = parseFloat(day.revenue) || 0;
          const height = Math.max((revenue / maxRevenue) * 160, 2);
          const date = new Date(day.date);
          const isToday = new Date().toDateString() === date.toDateString();

          return (
            <div
              key={idx}
              className="d-flex flex-column align-items-center flex-grow-1"
              style={{ minWidth: 0 }}
              title={`${date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}: ${formatCurrency(revenue)} (${day.orders} orders)`}
            >
              <div
                style={{
                  width: '100%',
                  maxWidth: '24px',
                  height: `${height}px`,
                  background: isToday
                    ? 'linear-gradient(180deg, #EAB308, #CA8A04)'
                    : 'linear-gradient(180deg, #DC2626, #B91C1C)',
                  borderRadius: '3px 3px 0 0',
                  opacity: revenue === 0 ? 0.2 : 0.85,
                  transition: 'height 0.3s ease',
                }}
              />
            </div>
          );
        })}
      </div>
      <div className="d-flex justify-content-between mt-2">
        <small className="text-muted">
          {new Date(displayData[0]?.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
        </small>
        <small className="text-muted">
          {new Date(displayData[displayData.length - 1]?.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
        </small>
      </div>
    </div>
  );
};

// Order status breakdown component
const StatusBreakdown = ({ data }) => {
  if (!data || data.length === 0) {
    return <small className="text-muted">No orders yet</small>;
  }

  const statusColors = {
    pending: '#F59E0B',
    confirmed: '#3B82F6',
    processing: '#8B5CF6',
    shipped: '#06B6D4',
    delivered: '#10B981',
    cancelled: '#EF4444',
    refunded: '#6B7280',
  };

  const total = data.reduce((sum, s) => sum + parseInt(s.count), 0);

  return (
    <div>
      {/* Stacked bar */}
      <div className="d-flex rounded overflow-hidden mb-3" style={{ height: '12px' }}>
        {data.map((s, idx) => (
          <div
            key={idx}
            style={{
              width: `${(parseInt(s.count) / total) * 100}%`,
              backgroundColor: statusColors[s.status] || '#9CA3AF',
              minWidth: parseInt(s.count) > 0 ? '4px' : '0',
            }}
            title={`${s.status}: ${s.count}`}
          />
        ))}
      </div>
      {/* Legend */}
      <div className="row g-2">
        {data.map((s, idx) => (
          <div key={idx} className="col-6 col-md-4">
            <div className="d-flex align-items-center gap-2">
              <span
                className="rounded-circle d-inline-block"
                style={{
                  width: '10px',
                  height: '10px',
                  backgroundColor: statusColors[s.status] || '#9CA3AF',
                  flexShrink: 0,
                }}
              />
              <small className="text-muted text-capitalize">
                {s.status}: <strong className="text-dark">{s.count}</strong>
              </small>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashRes, revRes] = await Promise.all([
          adminService.getDashboard(),
          adminService.getRevenue(),
        ]);
        setStats(dashRes.data.data);
        setRevenue(revRes.data.data);
      } catch (error) {
        console.error('Error fetching admin data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <LoadingSpinner />;

  // Compute quick stats from revenue data
  const totalRevenue = revenue?.totalRevenue || 0;
  const monthlyRevenue = revenue?.monthlyRevenue || 0;
  const dailyData = revenue?.dailyRevenue || [];
  const todayData = dailyData.length > 0 ? dailyData[dailyData.length - 1] : null;
  const todayRevenue = todayData ? parseFloat(todayData.revenue) : 0;
  const totalOrdersFromRevenue = dailyData.reduce((sum, d) => sum + parseInt(d.orders || 0), 0);

  return (
    <div>
      <h2 className="mb-4">Admin Dashboard</h2>

      {/* Platform Stats */}
      <div className="row g-3 mb-4">
        <div className="col-sm-6 col-lg-3">
          <div className="dashboard-card text-center">
            <FiUsers size={28} className="text-primary mb-2" />
            <div className="dashboard-stat">{stats?.totalUsers || 0}</div>
            <div className="dashboard-label">Total Users</div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-3">
          <div className="dashboard-card text-center">
            <FiHome size={28} className="text-success mb-2" />
            <div className="dashboard-stat">{stats?.totalStores || 0}</div>
            <div className="dashboard-label">Stores</div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-3">
          <div className="dashboard-card text-center">
            <FiPackage size={28} className="text-warning mb-2" />
            <div className="dashboard-stat">{stats?.totalProducts || 0}</div>
            <div className="dashboard-label">Products</div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-3">
          <div className="dashboard-card text-center">
            <FiShoppingBag size={28} className="text-info mb-2" />
            <div className="dashboard-stat">{stats?.totalOrders || 0}</div>
            <div className="dashboard-label">Orders</div>
          </div>
        </div>
      </div>

      {stats?.pendingStores > 0 && (
        <div className="alert alert-warning mb-4">
          <strong>{stats.pendingStores}</strong> merchant store(s) pending approval.
        </div>
      )}

      {/* Revenue Stats */}
      <div className="row g-3 mb-4">
        <div className="col-sm-6 col-lg-4">
          <div className="dashboard-card">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded"
                style={{ width: '48px', height: '48px', background: 'rgba(185, 28, 28, 0.1)' }}
              >
                <FiDollarSign size={24} className="text-primary" />
              </div>
              <div>
                <div className="dashboard-label">Total Revenue</div>
                <div className="dashboard-stat fs-4">{formatCurrency(totalRevenue)}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-4">
          <div className="dashboard-card">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded"
                style={{ width: '48px', height: '48px', background: 'rgba(234, 179, 8, 0.1)' }}
              >
                <FiCalendar size={24} style={{ color: '#EAB308' }} />
              </div>
              <div>
                <div className="dashboard-label">This Month</div>
                <div className="dashboard-stat fs-4">{formatCurrency(monthlyRevenue)}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-lg-4">
          <div className="dashboard-card">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded"
                style={{ width: '48px', height: '48px', background: 'rgba(16, 185, 129, 0.1)' }}
              >
                <FiTrendingUp size={24} className="text-success" />
              </div>
              <div>
                <div className="dashboard-label">Today</div>
                <div className="dashboard-stat fs-4">{formatCurrency(todayRevenue)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="row g-3">
        {/* Daily Revenue Chart */}
        <div className="col-lg-8">
          <div className="dashboard-card">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0 fw-bold">Revenue — Last 30 Days</h6>
              <small className="text-muted">
                {totalOrdersFromRevenue} order{totalOrdersFromRevenue !== 1 ? 's' : ''}
              </small>
            </div>
            <BarChart data={dailyData} />
          </div>
        </div>

        {/* Orders by Status */}
        <div className="col-lg-4">
          <div className="dashboard-card">
            <h6 className="mb-3 fw-bold">Orders by Status</h6>
            <StatusBreakdown data={revenue?.ordersByStatus} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
